import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  createDefectGalleryImage,
  deleteDefectGalleryImage,
  fetchDefectGallery,
  updateDefectGalleryImage,
} from "../../services/defectGalleryApi";
import { fetchCategories } from "../../services/deviceApi";
import { uploadImage } from "../../services/uploadApi";
import "../../styles/admin-common.css";

const FILTER_TAGS = ["SCREEN", "BATTERY", "BODY", "CAMERA", "PERFORMANCE", "ACCESSORIES", "OTHER"];

const empty = {
  name: "",
  category: "SCREEN",
  productCategoryId: "",
  imageUrl: "",
  isActive: true,
};

const DefectGallery = () => {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filterCategoryId, setFilterCategoryId] = useState("");
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const fileInputRef = useRef(null);
  const previewObjectUrlRef = useRef(null);

  const clearPreviewObjectUrl = () => {
    if (previewObjectUrlRef.current) {
      URL.revokeObjectURL(previewObjectUrlRef.current);
      previewObjectUrlRef.current = null;
    }
  };

  const resetForm = (defaultCategoryId = "") => {
    clearPreviewObjectUrl();
    setPendingFile(null);
    setPreviewUrl("");
    setEditingId(null);
    setForm({
      ...empty,
      productCategoryId: defaultCategoryId,
    });
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const load = async () => {
    setLoading(true);
    try {
      setItems(await fetchDefectGallery(false));
    } catch (err) {
      alert(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    fetchCategories()
      .then((cats) => {
        setCategories(cats);
        if (cats.length > 0) {
          const mobile = cats.find((c) => c.name?.toLowerCase() === "mobile") || cats[0];
          setFilterCategoryId(String(mobile.id));
          resetForm(String(mobile.id));
        }
      })
      .catch(() => {});
    return () => clearPreviewObjectUrl();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const visibleItems = useMemo(() => {
    if (!filterCategoryId) return items;
    return items.filter(
      (item) =>
        !item.productCategoryId || String(item.productCategoryId) === String(filterCategoryId)
    );
  }, [items, filterCategoryId]);

  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    clearPreviewObjectUrl();
    const objectUrl = URL.createObjectURL(file);
    previewObjectUrlRef.current = objectUrl;
    setPendingFile(file);
    setPreviewUrl(objectUrl);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.productCategoryId) {
      alert("Select a product type (Mobile, Tablet, etc.)");
      return;
    }

    let imageUrl = form.imageUrl.trim();
    if (pendingFile) {
      setUploading(true);
      try {
        const uploaded = await uploadImage(pendingFile, "defect-images");
        imageUrl = uploaded.url;
      } catch (err) {
        alert(err.message);
        setUploading(false);
        return;
      }
      setUploading(false);
    }

    if (!imageUrl) {
      alert("Choose an image to upload");
      return;
    }

    try {
      const payload = {
        name: form.name.trim(),
        category: form.category.trim() || null,
        productCategoryId: Number(form.productCategoryId),
        imageUrl,
        isActive: form.isActive,
      };
      if (editingId) {
        const result = await updateDefectGalleryImage(editingId, payload);
        if (result?.syncedDefectCount > 0) {
          alert(`Updated — refreshed image on ${result.syncedDefectCount} linked defect(s).`);
        }
      } else {
        await createDefectGalleryImage(payload);
      }
      const savedCategoryId = form.productCategoryId;
      resetForm(savedCategoryId);
      setFilterCategoryId(savedCategoryId);
      await load();
    } catch (err) {
      alert(err.message);
    }
  };

  const categoryName = (id) =>
    categories.find((c) => String(c.id) === String(id))?.name || "—";

  const startEdit = (item) => {
    clearPreviewObjectUrl();
    setPendingFile(null);
    setPreviewUrl(item.imageUrl || "");
    setEditingId(item.id);
    setForm({
      name: item.name || "",
      category: item.category || "SCREEN",
      productCategoryId: item.productCategoryId ? String(item.productCategoryId) : "",
      imageUrl: item.imageUrl || "",
      isActive: item.isActive !== false,
    });
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const defaultCategoryId =
    categories.find((c) => c.name?.toLowerCase() === "mobile")?.id || categories[0]?.id;

  return (
    <div className="admin-page">
      <h2>Defect gallery</h2>
      <p className="subtitle">
        Image library only — <strong>label + image</strong> for the dropdown on{" "}
        <Link to="/defects">Central defects</Link>. Does not create defects.
        {" "}Groups: <Link to="/defect-groups">Defect groups</Link>
      </p>

      <div className="admin-filters">
        <label className="field-label">
          Filter by product type
          <select value={filterCategoryId} onChange={(e) => setFilterCategoryId(e.target.value)}>
            <option value="">All types</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </label>
      </div>

      <div className="admin-card">
        <h3>{editingId ? "Edit gallery image" : "Add gallery image"}</h3>
        <form className="admin-form admin-form-grid" onSubmit={handleSubmit}>
          <label className="field-label">
            Product type
            <select
              value={form.productCategoryId}
              onChange={(e) => setForm({ ...form, productCategoryId: e.target.value })}
              required
            >
              <option value="">Select type</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </label>

          <label className="field-label">
            Filter tag
            <select
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
            >
              {FILTER_TAGS.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </label>

          <label className="field-label span-2">
            Dropdown label
            <input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Name shown in image dropdown"
              required
            />
          </label>

          <div className="field-label span-2 image-upload-field">
            Image
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              style={{ display: "none" }}
              onChange={handleFileSelect}
            />
            <div
              className={`image-upload-zone${previewUrl ? " has-image" : ""}`}
              role="button"
              tabIndex={0}
              onClick={() => !previewUrl && fileInputRef.current?.click()}
              onKeyDown={(event) => {
                if (!previewUrl && (event.key === "Enter" || event.key === " ")) {
                  event.preventDefault();
                  fileInputRef.current?.click();
                }
              }}
            >
              {previewUrl ? (
                <>
                  <img src={previewUrl} alt="Preview" className="image-upload-preview" />
                  <div>
                    <button
                      type="button"
                      className="admin-btn secondary"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      Replace image
                    </button>
                    {pendingFile ? (
                      <p className="field-hint" style={{ marginTop: 8, marginBottom: 0 }}>
                        New image selected — uploads when you save.
                      </p>
                    ) : null}
                  </div>
                </>
              ) : (
                <div className="image-upload-placeholder">
                  <strong>Choose image</strong>
                  JPEG, PNG, WebP or GIF · uploads on save
                </div>
              )}
            </div>
          </div>

          <label className="checkbox-field span-2">
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
            />
            Active
          </label>

          <div className="form-actions span-2">
            <button type="submit" className="admin-btn" disabled={uploading}>
              {uploading ? "Uploading…" : editingId ? "Update" : "Add to gallery"}
            </button>
            {editingId ? (
              <button
                type="button"
                className="admin-btn secondary"
                onClick={() => resetForm(defaultCategoryId ? String(defaultCategoryId) : "")}
              >
                Cancel
              </button>
            ) : null}
          </div>
        </form>
      </div>

      {loading ? <div className="admin-loading">Loading gallery</div> : (
        visibleItems.length === 0 ? (
          <div className="admin-card empty-state-card">
            <p>No gallery images yet. Add label + image above for the defects dropdown.</p>
          </div>
        ) : (
          <div className="gallery-grid">
            {visibleItems.map((item) => (
              <div key={item.id} className="admin-card gallery-card">
                <img src={item.imageUrl} alt={item.name} />
                <strong className="gallery-card-title">{item.name}</strong>
                <small>
                  {item.productCategoryName || categoryName(item.productCategoryId)} · {item.category || "—"} ·{" "}
                  {item.isActive ? "Active" : "Inactive"}
                  {item.linkedDefectCount > 0 ? ` · used by ${item.linkedDefectCount} defect(s)` : ""}
                </small>
                <div className="gallery-card-actions">
                  <button type="button" className="admin-btn secondary" onClick={() => startEdit(item)}>
                    Edit
                  </button>
                  <button
                    type="button"
                    className="admin-btn danger"
                    onClick={async () => {
                      const linked = item.linkedDefectCount ?? 0;
                      const msg =
                        linked > 0
                          ? `Delete "${item.name}"?\n\nUsed by ${linked} defect(s) — their gallery link/icon will be cleared, and the image file will be removed from storage.`
                          : `Delete "${item.name}"?\n\nThe image file will also be removed from storage.`;
                      if (!window.confirm(msg)) return;
                      try {
                        await deleteDefectGalleryImage(item.id);
                        setItems((prev) => prev.filter((g) => g.id !== item.id));
                        if (editingId === item.id) {
                          resetForm(filterCategoryId || form.productCategoryId);
                        }
                      } catch (err) {
                        alert(err.message);
                      }
                    }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
};

export default DefectGallery;
